import {
  ArtisDataGrid,
  type ArtisDataGridRef,
  CompactSelection,
  type DataEditorProps,
  type DrawCellCallback,
  drawRegionKnobs,
  drawSpreadsheetHeader,
  type GridCell,
  type GridColumn,
  type Item,
  type Rectangle,
} from "@artis/data-grid";
import type { ModuleStorage } from "@artis/sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ConfirmDialog } from "../../webapp/components/ConfirmDialog";
import { ContextMenu } from "../../webapp/ui/context-menu";
import { cellContent } from "./cellContent";
import CellEditor from "./CellEditor";
import { type ScratchpadMenuContext, scratchpadContextMenu } from "./contextMenu";
import { formatValue } from "./formatValue";
import FormulaBar from "./FormulaBar";
import { selectionItems, selectionRangeLabel } from "./selection";
import SheetTabs from "./SheetTabs";

import { colToLabel, sheetNamesEqual, type PersistedWorkbook } from "@package/scratchpad-utils";
import { useMediaQuery } from "../../utils/react";
import { MOBILE_NAV_MEDIA } from "../../webapp/layout/MobileNavBar";
import { extractFormulaRefs } from "./formula/highlight";
import { rangeRect } from "./formula/picking";
import { useAutosave } from "./hooks/useAutosave";
import { useClearCells } from "./hooks/useClearCells";
import { useClipboard } from "./hooks/useClipboard";
import { useCurveWatcher } from "./hooks/useCurveWatcher";
import { useDeleteSheetConfirm } from "./hooks/useDeleteSheetConfirm";
import { useRemoteWorkbook } from "./hooks/useRemoteWorkbook";
import { useEditSession } from "./hooks/useEditSession";
import { useMarketPick } from "./hooks/useMarketPick";
import { useFill } from "./hooks/useFill";
import { usePickMode } from "./hooks/usePickMode";
import { itemKey, useWorkbook } from "./hooks/useWorkbook";

const PICK_GRID_THEME = { accentColor: "#00000000", accentLight: "#00000000" };

type ScratchpadGridHost = HTMLDivElement & {
  __scratchpadGetBounds?: (
    col: number,
    row: number,
  ) => { x: number; y: number; width: number; height: number } | undefined;
};

const MAX_COLS = 702;
const MAX_ROWS = 1_048_576;
const START_COLS = 20;
const START_ROWS = 60;
const COL_BUFFER = 5;
const HEADER_HEIGHT = 26;
const ROW_BUFFER = 10;

export default function Scratchpad({
  storage,
  initial,
}: {
  storage: ModuleStorage;
  initial: PersistedWorkbook | null;
}) {
  const readOnly = useMediaQuery(MOBILE_NAV_MEDIA);
  const {
    sheets,
    activeSheet,
    activeSheetId,
    setActiveSheetId,
    curves,
    subscribeExternals,
    getValue,
    getNode,
    setValue,
    moveRange,
    transact,
    undo,
    redo,
    setColWidth,
    replaceWorkbook,
    ...sheetActions
  } = useWorkbook(initial);

  useCurveWatcher({ curves, subscribeExternals });
  const { markPersisted } = useAutosave(storage, sheets, activeSheetId);

  const deleteConfirm = useDeleteSheetConfirm({ storage, sheets, deleteSheet: sheetActions.deleteSheet });

  const [view, setView] = useState({ rows: START_ROWS, cols: START_COLS });

  const rowCount = Math.min(MAX_ROWS, Math.max(activeSheet.used.rows, view.rows) + ROW_BUFFER);
  const colCount = Math.min(MAX_COLS, Math.max(activeSheet.used.cols, view.cols) + COL_BUFFER);

  const colWidths = activeSheet.colWidths;
  const columns = useMemo<GridColumn[]>(
    () =>
      Array.from({ length: colCount }, (_, i) => {
        const title = colToLabel(i);
        return { title, id: title, width: colWidths?.[i] ?? 120 };
      }),
    [colCount, colWidths],
  );

  const onColumnResize = useCallback(
    (_: GridColumn, newSize: number, colIndex: number) => setColWidth(colIndex, newSize),
    [setColWidth],
  );

  const onVisibleRegionChanged = useCallback((range: Rectangle) => {
    setView((prev) => {
      const rows = range.y + range.height;
      const cols = range.x + range.width;
      if (rows === prev.rows && cols === prev.cols) return prev;
      return { rows, cols };
    });
  }, []);

  const session = useEditSession({ setValue, rowCount, colCount });
  const {
    selection,
    setSelection,
    activeCell,
    activeCellRef,
    selectionRef,
    draft,
    setDraft,
    draftRef,
    caretRef,
    barElRef,
    gridWrapRef,
    editorFocusChanged,
    beginEdit,
    attachCellEditor,
    commitDraft,
    cancelEdit,
    highlightText,
  } = session;

  useRemoteWorkbook({
    storage,
    sheets,
    activeSheetId,
    editing: draft !== null,
    replaceWorkbook,
    markPersisted,
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<ArtisDataGridRef | null>(null);

  useEffect(() => {
    const el = gridWrapRef.current as ScratchpadGridHost | null;
    if (!el) return;
    el.__scratchpadGetBounds = (col, row) => gridRef.current?.getBounds(col, row);
    return () => {
      delete el.__scratchpadGetBounds;
    };
  });
  const activeSheetNameRef = useRef(activeSheet.name);
  activeSheetNameRef.current = activeSheet.name;

  const { pickActive } = usePickMode(session, { gridRef, activeSheetNameRef });

  useMarketPick(session);

  const getDisplay = useCallback(
    (cell: Item): string => {
      const node = getNode(cell);
      return node?.error ? node.error.code : (formatValue(node?.value) ?? getValue(cell));
    },
    [getNode, getValue],
  );

  const getCellContent = useCallback(
    (cell: Item): GridCell => cellContent(cell, draft, { getSource: getValue, getDisplay }),
    [getValue, getDisplay, draft],
  );

  const { clipRegion, getCellsForSelection, onDelete, onPaste } = useClipboard(session, {
    activeSheetId,
    getSource: getValue,
    getDisplay,
    setValue,
    moveRange,
    transact,
  });

  const onFillPattern = useFill({ getSource: getValue, setValue, transact });

  const clearCells = useClearCells({ getSource: getValue, setValue, transact, rowCount, colCount });

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el || readOnly) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || draftRef.current) return;
      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (key === "y") {
        e.preventDefault();
        redo();
      }
    };
    el.addEventListener("keydown", onKeyDown, { capture: true });
    return () => el.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [gridWrapRef, draftRef, undo, redo, readOnly]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!draftRef.current) return;
      const t = e.target;
      if (t instanceof Element && (t.closest("[data-formula-bar]") || t.closest("[data-cell-editor]"))) return;
      commitDraft();
    };
    el.addEventListener("pointerdown", onPointerDown);
    return () => el.removeEventListener("pointerdown", onPointerDown);
  }, [draftRef, commitDraft]);

  const onCellActivated = useCallback(
    (cell: Item) => {
      if (readOnly || draftRef.current) return;
      beginEdit(cell, getValue(cell), "cell");
    },
    [draftRef, beginEdit, getValue, readOnly],
  );

  const onGridKeyDown = useCallback<NonNullable<DataEditorProps["onKeyDown"]>>(
    (e) => {
      if (readOnly || draftRef.current) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        if (clearCells(selectionRef.current)) {
          e.cancel();
          e.preventDefault();
        }
        return;
      }
      const cell = activeCellRef.current;
      if (!cell) return;
      if (e.key === "F2") {
        e.cancel();
        beginEdit(cell, getValue(cell), "cell");
        return;
      }
      if (!e.metaKey && !e.ctrlKey && e.key.length === 1 && /[ -~]/.test(e.key)) {
        e.cancel();
        e.preventDefault();
        beginEdit(cell, e.key, "cell");
      }
    },
    [draftRef, activeCellRef, selectionRef, clearCells, beginEdit, getValue, readOnly],
  );

  const onColumnResizeStart = useCallback(() => commitDraft(), [commitDraft]);

  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);

  const onCellContextMenu = useCallback<NonNullable<DataEditorProps["onCellContextMenu"]>>(
    (cell, event) => {
      event.preventDefault();
      const key = itemKey(cell);
      const within = selectionItems(selection).some((item) => itemKey(item) === key);
      if (!within) {
        setSelection({
          columns: CompactSelection.empty(),
          rows: CompactSelection.empty(),
          current: { cell, range: { x: cell[0], y: cell[1], width: 1, height: 1 }, rangeStack: [] },
        });
      }
      setMenuPosition({ x: event.bounds.x + event.localEventX, y: event.bounds.y + event.localEventY });
    },
    [selection, setSelection],
  );

  const menuContext = useMemo<ScratchpadMenuContext>(() => {
    const byKey = new Map(selectionItems(selection).map((item) => [itemKey(item), item] as const));
    const cells: ScratchpadMenuContext["cells"] = [...byKey.entries()].flatMap(([key, item]) => {
      const src = getValue(item);
      return src === "" ? [] : [{ key, src, error: getNode(item)?.error ?? null }];
    });
    return {
      cells,
      applyEdits: (edits) => {
        transact(() => {
          for (const { key, src } of edits) {
            const item = byKey.get(key);
            if (item) setValue(item, src);
          }
        });
      },
    };
  }, [selection, getValue, getNode, transact, setValue]);

  const refRegions = useMemo(() => {
    if (highlightText === null) return [];
    const seen = new Set<string>();
    const regions: Array<{ color: string; range: Rectangle }> = [];
    for (const ref of extractFormulaRefs(highlightText)) {
      if (ref.addr.start.sheet && !sheetNamesEqual(ref.addr.start.sheet, activeSheet.name)) continue;
      const range = rangeRect(ref.addr);
      const key = `${range.x},${range.y},${range.width},${range.height}`;
      if (seen.has(key)) continue;
      seen.add(key);
      regions.push({ color: ref.color, range });
    }
    return regions;
  }, [highlightText, activeSheet.name]);

  const clipRange = clipRegion && clipRegion.sheetId === activeSheetId ? clipRegion.range : null;
  const cutRange = clipRegion?.mode === "cut" ? clipRange : null;

  const highlightRegions = useMemo(() => {
    const regions: Array<{ color: string; range: Rectangle; style: "solid" | "dashed" }> = refRegions.map((r) => ({
      color: `${r.color}33`,
      range: r.range,
      style: "solid",
    }));
    if (clipRange) {
      regions.push({ color: "#21734600", range: clipRange, style: "dashed" });
    }
    return regions.length ? regions : undefined;
  }, [refRegions, clipRange]);

  const drawCell = useMemo<DrawCellCallback>(() => {
    const knobs = drawRegionKnobs(refRegions);
    const cut = cutRange;
    return (args, drawContent) => {
      const inCut =
        cut && args.col >= cut.x && args.col < cut.x + cut.width && args.row >= cut.y && args.row < cut.y + cut.height;
      const paint = inCut
        ? () => {
            args.ctx.save();
            args.ctx.globalAlpha = 0.4;
            drawContent();
            args.ctx.restore();
          }
        : drawContent;
      knobs(args, paint);
    };
  }, [refRegions, cutRange]);

  const cellRef = activeCell ? itemKey(activeCell) : "";
  const formulaValue = draft?.text ?? (activeCell ? getValue(activeCell) : "");
  const displayValue = activeCell ? getDisplay(activeCell) : "";

  return (
    <div
      ref={rootRef}
      data-testid="scratchpad"
      data-readonly={readOnly ? "true" : "false"}
      data-active-cell={cellRef}
      data-display-value={displayValue}
      data-source-value={formulaValue}
      data-selection={selectionRangeLabel(selection)}
      data-artis-ui
      className="flex h-full w-full flex-col"
    >
      <FormulaBar
        cellRef={cellRef}
        value={formulaValue}
        disabled={readOnly || !activeCell}
        inputRef={(el) => {
          barElRef.current = el;
        }}
        onCaretChange={(c) => {
          caretRef.current = c;
        }}
        onChange={(text) => {
          setDraft((d) => {
            const c = d?.cell ?? activeCellRef.current;
            return c ? { cell: c, text } : null;
          });
        }}
        onCommit={(movement) => {
          commitDraft(movement);
          barElRef.current?.blur();
          gridRef.current?.focus();
        }}
        onCancel={() => {
          cancelEdit();
          gridRef.current?.focus();
        }}
        onFocusChange={(focused) => {
          editorFocusChanged("bar", focused);
          if (focused && !readOnly && !draftRef.current) {
            const c = activeCellRef.current;
            if (c) beginEdit(c, getValue(c), "bar");
          }
        }}
      />
      <div ref={gridWrapRef} data-testid="scratchpad-grid" className="relative min-h-0 flex-1">
        <ArtisDataGrid
          ref={gridRef}
          columns={columns}
          rows={rowCount}
          getCellContent={getCellContent}
          getCellsForSelection={getCellsForSelection}
          onDelete={readOnly ? undefined : onDelete}
          onPaste={readOnly ? false : onPaste}
          fillHandle={!readOnly}
          onFillPattern={readOnly ? undefined : onFillPattern}
          onCellActivated={onCellActivated}
          onCellContextMenu={readOnly ? undefined : onCellContextMenu}
          onKeyDown={onGridKeyDown}
          cellActivationBehavior="double-click"
          onVisibleRegionChanged={onVisibleRegionChanged}
          highlightRegions={highlightRegions}
          drawCell={drawCell}
          drawHeader={drawSpreadsheetHeader}
          gridSelection={selection}
          columnSelect="none"
          onColumnResize={readOnly ? undefined : onColumnResize}
          onColumnResizeStart={onColumnResizeStart}
          onGridSelectionChange={setSelection}
          drawFocusRing={!pickActive}
          theme={pickActive ? PICK_GRID_THEME : undefined}
          rowMarkers="number"
          headerHeight={HEADER_HEIGHT}
        />
        <div
          data-testid="scratchpad-header-line"
          className="pointer-events-none absolute inset-x-0 h-px bg-[var(--colour-border-default)]"
          style={{ top: HEADER_HEIGHT - 1 }}
        />
        {draft && !readOnly && (
          <CellEditor
            gridRef={gridRef}
            cell={draft.cell}
            text={draft.text}
            inputRef={attachCellEditor}
            onCaretChange={(c) => {
              caretRef.current = c;
            }}
            onChange={(text) => {
              setDraft((d) => (d ? { cell: d.cell, text } : d));
            }}
            onCommit={(movement) => {
              commitDraft(movement);
              gridRef.current?.focus();
            }}
            onCancel={() => {
              cancelEdit();
              gridRef.current?.focus();
            }}
            onFocus={() => editorFocusChanged("cell", true)}
            onBlur={() => editorFocusChanged("cell", false)}
          />
        )}
        {menuPosition && (
          <ContextMenu
            items={scratchpadContextMenu}
            context={menuContext}
            position={menuPosition}
            onClose={() => setMenuPosition(null)}
          />
        )}
      </div>
      <SheetTabs
        sheets={sheets}
        activeId={activeSheetId}
        onSelect={setActiveSheetId}
        onAdd={sheetActions.addSheet}
        onDelete={deleteConfirm.requestDelete}
        onRename={sheetActions.renameSheet}
        readOnly={readOnly}
      />
      {deleteConfirm.pending && (
        <ConfirmDialog
          open
          title="Delete Sheet?"
          message={`You're about to permanently delete ${deleteConfirm.pending.name}. This action can't be undone.`}
          confirmButtonText="Delete Sheet"
          variant="danger"
          showCheckbox
          checkboxLabel="Don't ask me this again"
          checkboxChecked={deleteConfirm.dontAskAgain}
          onCheckboxChange={deleteConfirm.setDontAskAgain}
          onCancel={deleteConfirm.cancel}
          onConfirm={deleteConfirm.confirm}
        />
      )}
    </div>
  );
}
